import * as React from "react";

import {
  Box,
  BoxProps,
  Checkbox,
  Divider,
  Flex,
  FormLabelProps,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputProps,
  InputRightElement,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  Switch,
  SwitchProps,
  chakra,
} from "@chakra-ui/react";
import { ChartInputSize, chartMeasures } from "modules";
import {
  ChartOptionProps,
  CheckboxButtonProps,
  CrossChartOptionProps,
  FormCalendarInput,
  FormChartOption,
  FormCheckboxButton,
  FormCounterInputOption,
  FormCrossChartOption,
  NumberSelectorProps,
  FormRichText,
  RichTextProps,
  Textarea,
  TextareaProps,
} from "shared";
import { CloseOutline, Recording } from "icons";
import {
  EmrCheckFilled,
  EmrRemove,
  EmrSendouts,
  EmrVoiceNote,
} from "@medstonetech/slate-icons";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import { useController, useFormContext } from "react-hook-form";

import InputMask from "react-input-mask";
import { useChartTrackingContext } from "contexts";
import { isValid } from "date-fns";

type ChartElementType =
  | "label"
  | "container"
  | "option"
  | "cross-option"
  | "input"
  | "text-area"
  | "rich-text"
  | "empty"
  | "circle-option"
  | "date"
  | "mask-date"
  | "checkbox"
  | "checkbox-button"
  | "counter-option"
  | "switch";

type ImportedType = "nursing" | "provider";

type ChartRowElementImportedProps = Partial<{
  date: string;
  status: "accepted" | "rejected" | "pending";
  type: ImportedType;
  value: string;
}>;

type BaseChartRowElementProps = {
  name: string;
};

type ChartRowElementLabelProps = {
  type: "label";
  label: string;
} & FormLabelProps;

type ChartRowElementContainerProps = {
  type: "container";
  content: React.ReactElement;
} & BoxProps;

type ChartRowElementInputProps = {
  type: "input";
  size?: ChartInputSize;
  imported?: ChartRowElementImportedProps;
  showSend?: boolean;
} & BaseChartRowElementProps &
  Omit<InputProps, "type" | "size"> & { inputType?: InputProps["type"] };

type ChartRowElementTextAreaProps = {
  type: "text-area";
  imported?: ChartRowElementImportedProps;
} & BaseChartRowElementProps &
  Omit<TextareaProps, "type">;

type ChartRowElementRichTextProps = {
  type: "rich-text";
  imported?: ChartRowElementImportedProps;
} & BaseChartRowElementProps &
  Omit<RichTextProps, "value" | "onChange" | "type">;

type ChartRowElementOptionProps = {
  type: "option";
  label: string;
  imported?: ChartRowElementImportedProps;
} & BaseChartRowElementProps &
  ChartOptionProps;

type ChartRowElementCrossOptionProps = {
  type: "cross-option";
  imported?: ChartRowElementImportedProps;
} & BaseChartRowElementProps &
  CrossChartOptionProps;

type ChartRowElementCircleOptionProps = Omit<
  ChartRowElementOptionProps,
  "type"
> & { type: "circle-option"; imported?: ChartRowElementImportedProps };

type ChartRowElementDateProps = {
  type: "date";
  dateFormat?: string;
  size?: ChartInputSize;
  showTimeInput?: boolean;
  isDisabled?: boolean;
  imported?: ChartRowElementImportedProps;
} & BaseChartRowElementProps &
  BoxProps;

type ChartRowElementEmptyProps = {
  type: "empty";
} & BoxProps;

type ChartRowElementCheckboxProps = {
  type: "checkbox";
  imported?: ChartRowElementImportedProps;
} & BaseChartRowElementProps &
  CheckboxButtonProps;

type ChartRowElementSwitchProps = {
  type: "switch";
  imported?: ChartRowElementImportedProps;
} & BaseChartRowElementProps &
  SwitchProps;

type ChartRowElementCheckboxButtonProps = {
  type: "checkbox-button";
  imported?: ChartRowElementImportedProps;
} & BaseChartRowElementProps &
  CheckboxButtonProps;

type ChartRowElementMaskDateProps = {
  type: "mask-date";
  size?: ChartInputSize;
  isDisabled?: boolean;
  imported?: ChartRowElementImportedProps;
} & BaseChartRowElementProps &
  BoxProps;

type ChartRowElementCounterOptionProps = {
  type: "counter-option";
} & BaseChartRowElementProps & {
    inputProps?: Omit<InputProps, "name">;
    inputName?: string;
    inputLabel?: string;
    imported?: ChartRowElementImportedProps;
  } & NumberSelectorProps &
  Pick<BoxProps, "children">;

type ChartRowElementProps =
  | ChartRowElementLabelProps
  | ChartRowElementContainerProps
  | ChartRowElementInputProps
  | ChartRowElementOptionProps
  | ChartRowElementCrossOptionProps
  | ChartRowElementTextAreaProps
  | ChartRowElementRichTextProps
  | ChartRowElementEmptyProps
  | ChartRowElementCircleOptionProps
  | ChartRowElementDateProps
  | ChartRowElementMaskDateProps
  | ChartRowElementCheckboxProps
  | ChartRowElementCheckboxButtonProps
  | ChartRowElementCounterOptionProps
  | ChartRowElementSwitchProps;

const IMPORTED_TYPE_COLORS: Record<ImportedType, string> = {
  nursing: "red-sizzling",
  provider: "blue",
};

function ChartRowElementImportPopover(
  props: React.PropsWithChildren<
    ChartRowElementImportedProps & { name: string }
  >
) {
  const { children, name, ...rest } = props;

  if (!rest.date) return <>{children}</>;

  return (
    <Popover isOpen placement="top">
      <PopoverTrigger>{children}</PopoverTrigger>
      <PopoverContent
        width="6.5rem"
        p="0.5rem"
        flexDir="row"
        justifyContent="space-around"
        shadow="dark-lg"
      >
        <PopoverArrow />

        <Icon
          as={EmrCheckFilled}
          color="green"
          fontSize="1.85rem"
          cursor="pointer"
        />

        <Divider orientation="vertical" minH="30px" />

        <Icon as={EmrRemove} color="red" fontSize="1.85rem" cursor="pointer" />
      </PopoverContent>
    </Popover>
  );
}

function isChartRowElementT<T extends ChartRowElementProps>(
  props: ChartRowElementProps,
  type: ChartElementType
): props is T {
  return props.type === type;
}

const ChartRowElementLabel: React.FC<ChartRowElementLabelProps> = (props) => {
  const { label, ...styleProps } = props;

  return (
    <chakra.span color="gray.700" padding="7px 10px" {...styleProps}>
      {label}
    </chakra.span>
  );
};

function ChartRowElementContainer(props: ChartRowElementContainerProps) {
  const { content, ...boxProps } = props;
  return (
    <Box p="9px" color="gray.700" {...boxProps}>
      {content}
    </Box>
  );
}

function ChartRowElementEmpty(props: ChartRowElementEmptyProps) {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { type: _type, ...boxProps } = props;

  return <Box {...boxProps} />;
}

export function useVoiceInput(onDone: (transcript: string) => void) {
  const [isRecording, setIsRecording] = React.useState(false);
  const { transcript, resetTranscript } = useSpeechRecognition();

  const start = React.useCallback(() => {
    resetTranscript();
    SpeechRecognition.startListening({ continuous: true });
    setIsRecording(true);
  }, [resetTranscript]);

  const stop = React.useCallback(() => {
    SpeechRecognition.stopListening();
    setIsRecording(false);
    onDone(transcript);
  }, [onDone, transcript]);

  React.useEffect(() => {
    if (!isRecording) resetTranscript();
  }, [isRecording, resetTranscript]);

  return {
    isRecording,
    transcript,
    start,
    stop,
  };
}

const VoiceControls = React.memo(
  ({
    isRecording,
    isDisabled,
    onStart,
    onStop,
  }: {
    isDisabled?: boolean;
    isRecording: boolean;
    onStart: () => void;
    onStop: () => void;
  }) => {
    return !isRecording ? (
      <IconButton
        isDisabled={isDisabled}
        aria-label="start recording"
        icon={<Icon as={EmrVoiceNote} color="gray.600" w="20px" h="20px" />}
        variant="ghost"
        minWidth="unset"
        onClick={onStart}
      />
    ) : (
      <>
        <IconButton
          aria-label="stop recording"
          icon={<Icon as={Recording} color="red" sx={{ fontSize: "32px" }} />}
          variant="ghost"
          ml={6}
          minWidth="unset"
          onClick={onStop}
        />
        <Icon as={EmrVoiceNote} color="gray.600" w="20px" h="20px" />
      </>
    );
  }
);

const ClearOrSendButton = React.memo(
  ({
    value,
    onClear,
    showSend,
  }: {
    value: string;
    onClear: () => void;
    showSend: boolean;
  }) => {
    if (!value) return null;

    return (
      <InputRightElement mr={2}>
        {showSend ? (
          <IconButton
            aria-label="send"
            variant="icon"
            icon={<EmrSendouts fill="gray.700" fontSize="28px" />}
            type="submit"
          />
        ) : (
          <IconButton
            aria-label="remove"
            variant="icon"
            icon={
              <CloseOutline
                color="gray.700"
                fontSize="28px"
                h="24px"
                w="24px"
              />
            }
            onClick={onClear}
          />
        )}
      </InputRightElement>
    );
  }
);

function ChartRowElementInput(props: ChartRowElementInputProps) {
  const {
    name,
    size,
    inputType,
    showSend = false,
    imported,
    onChange: onChangeProp,
    isDisabled,
    ...styleProps
  } = props;

  const { control, setValue, getValues } = useFormContext();
  const {
    field: { ref, value = "", onBlur },
  } = useController({ name, control });

  const onVoiceInputDone = React.useCallback(
    (transcript: string) => {
      const current = (getValues(name) ?? "") as string;
      const next = `${current} ${transcript}`.trim();
      setDraft(next);
      setValue(name, next, { shouldDirty: true });
    },
    [getValues, name, setValue]
  );

  const { isRecording, transcript, start, stop } =
    useVoiceInput(onVoiceInputDone);

  const [haveEnoughSpace, setHaveEnoughSpace] = React.useState(true);

  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const [draft, setDraft] = React.useState<string>(String(value ?? ""));

  React.useEffect(() => {
    if (!isRecording) {
      setDraft(String(value ?? ""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isRecording]);

  React.useEffect(() => {
    if (inputRef.current) {
      setHaveEnoughSpace(inputRef.current.offsetWidth > 100);
    }
  }, []);

  const mergedRef = React.useCallback(
    (el: HTMLInputElement | null) => {
      inputRef.current = el;
      ref(el);
    },
    [ref]
  );

  const recordingValue = isRecording ? `${draft} ${transcript}` : undefined;

  const clearInput = () => {
    setDraft("");
    setValue(name, "", { shouldDirty: true });
    inputRef.current?.focus();
  };

  const borderColor = imported
    ? IMPORTED_TYPE_COLORS[imported.type || "nursing"]
    : draft
    ? "blue"
    : "gray.700";

  const inputWidth = size && chartMeasures.CHART_INPUT_WIDTH[size];

  const inputPaddingLeft = React.useMemo(() => {
    if (isRecording && haveEnoughSpace) return "64px";
    if (!isRecording && !haveEnoughSpace) return "0.5rem";
    return "2rem";
  }, [isRecording, haveEnoughSpace]);

  const inputGroupProps = styleProps;

  return (
    <InputGroup width={inputWidth} {...inputGroupProps}>
      {haveEnoughSpace && (
        <InputLeftElement>
          <VoiceControls
            isDisabled={isDisabled}
            isRecording={isRecording}
            onStart={start}
            onStop={stop}
          />
        </InputLeftElement>
      )}

      <Input
        ref={mergedRef}
        disabled={isRecording || isDisabled}
        autoComplete="off"
        type={inputType}
        value={recordingValue ?? draft}
        onChange={(e) => {
          const next = e.target.value;
          setDraft(next);
          onChangeProp?.(e);
        }}
        onBlur={(e) => {
          setValue(name, draft, { shouldDirty: true });
          onBlur();
          styleProps.onBlur?.(e as React.FocusEvent<HTMLInputElement>);
        }}
        bg="white"
        borderColor={borderColor}
        color={borderColor}
        border="1px solid"
        pl={inputPaddingLeft}
      />

      {haveEnoughSpace && (
        <ClearOrSendButton
          value={draft}
          onClear={clearInput}
          showSend={showSend}
        />
      )}
    </InputGroup>
  );
}

function ChartRowElementRichText(props: ChartRowElementRichTextProps) {
  const {
    name,
    type: _type,
    imported: _imported,
    isDisabled,
    ...styleProps
  } = props;
  const { control } = useFormContext();

  return (
    <FormRichText
      name={name}
      control={control}
      isDisabled={isDisabled}
      {...styleProps}
    />
  );
}

function ChartRowElementTextArea(props: ChartRowElementTextAreaProps) {
  const { name, imported, isDisabled, ...styleProps } = props;
  const { register, setValue, watch } = useFormContext();
  const value = watch(name);

  const [recordingValue, setRecordingValue] = React.useState("");

  const [isRecording, setIsRecording] = React.useState(false);
  const { transcript, resetTranscript } = useSpeechRecognition();

  React.useEffect(() => {
    if (isRecording) {
      setRecordingValue(transcript);
    }
  }, [transcript, isRecording, setRecordingValue, name]);

  const newValue = `${value} ${recordingValue}`;

  if (imported)
    return (
      <ChartRowElementImportPopover name={name} {...imported}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "start",
            gap: 1,
            p: 1,
            border: "1px solid #D1D1D6",
            borderRadius: "10px",
          }}
        >
          <Box>
            {!isRecording && (
              <IconButton
                aria-label="start recording"
                icon={
                  <Icon as={EmrVoiceNote} color="gray.600" w="24px" h="24px" />
                }
                width="28px"
                height="20px"
                variant="ghost"
                minWidth="unset"
                mt={2}
                isDisabled={isDisabled}
                onClick={() => {
                  resetTranscript();
                  SpeechRecognition.startListening({ continuous: true });
                  setIsRecording(true);
                }}
              />
            )}
            {isRecording && (
              <Box display="flex" alignItems="center" justifyContent="start">
                <IconButton
                  aria-label="stop recording"
                  icon={
                    <Icon
                      as={Recording}
                      color="red"
                      sx={{ fontSize: "32px" }}
                    />
                  }
                  variant="ghost"
                  minWidth="unset"
                  onClick={() => {
                    SpeechRecognition.stopListening();
                    setIsRecording(false);
                    setValue(name, newValue, { shouldDirty: true });
                  }}
                />
                <Icon as={EmrVoiceNote} color="gray.600" w="24px" h="24px" />
              </Box>
            )}
          </Box>
          {!isRecording && (
            <Textarea
              bg="white"
              {...styleProps}
              {...register(name)}
              isDisabled={isDisabled}
              sx={{
                borderColor: "transparent",
                _focus: { borderColor: "transparent", bg: "white" },
                _hover: { bg: "white" },
                p: 0,
              }}
            />
          )}
          {isRecording && (
            <Textarea
              bg="white"
              isDisabled={isDisabled}
              {...styleProps}
              sx={{
                borderColor: "transparent",
                _focus: { borderColor: "transparent", bg: "white" },
                _hover: { bg: "white" },
                p: 0,
              }}
              value={newValue}
            />
          )}
          <Box>
            {value && (
              <IconButton
                aria-label="remove"
                variant="icon"
                w="32px"
                isDisabled={isDisabled}
                icon={
                  <CloseOutline
                    color="gray.700"
                    fontSize="28px"
                    h="24px"
                    w="24px"
                  />
                }
                onClick={() => {
                  setValue(name, "", { shouldDirty: true });
                }}
              />
            )}
          </Box>
        </Box>
      </ChartRowElementImportPopover>
    );

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "start",
        gap: 1,
        p: 1,
        border: "1px solid #D1D1D6",
        borderRadius: "10px",
      }}
    >
      <Box>
        {!isRecording && (
          <IconButton
            aria-label="start recording"
            icon={<Icon as={EmrVoiceNote} color="gray.600" w="24px" h="24px" />}
            width="28px"
            height="20px"
            variant="ghost"
            minWidth="unset"
            mt={2}
            isDisabled={isDisabled}
            onClick={() => {
              resetTranscript();
              SpeechRecognition.startListening({ continuous: true });
              setIsRecording(true);
            }}
          />
        )}
        {isRecording && (
          <Box display="flex" alignItems="center" justifyContent="start">
            <IconButton
              aria-label="stop recording"
              icon={
                <Icon as={Recording} color="red" sx={{ fontSize: "32px" }} />
              }
              variant="ghost"
              minWidth="unset"
              onClick={() => {
                SpeechRecognition.stopListening();
                setIsRecording(false);
                setValue(name, newValue, { shouldDirty: true });
              }}
            />
            <Icon as={EmrVoiceNote} color="gray.600" w="24px" h="24px" />
          </Box>
        )}
      </Box>
      {!isRecording && (
        <Textarea
          bg="white"
          isDisabled={isDisabled}
          {...styleProps}
          {...register(name)}
          sx={{
            borderColor: "transparent",
            _focus: { borderColor: "transparent", bg: "white" },
            _hover: { bg: "white" },
            p: 0,
          }}
        />
      )}
      {isRecording && (
        <Textarea
          bg="white"
          isDisabled={isDisabled}
          {...styleProps}
          sx={{
            borderColor: "transparent",
            _focus: { borderColor: "transparent", bg: "white" },
            _hover: { bg: "white" },
            p: 0,
          }}
          value={newValue}
        />
      )}
      <Box>
        {value && (
          <IconButton
            isDisabled={isDisabled}
            aria-label="remove"
            variant="icon"
            w="32px"
            icon={
              <CloseOutline
                color="gray.700"
                fontSize="28px"
                h="24px"
                w="24px"
              />
            }
            onClick={() => {
              setValue(name, "", { shouldDirty: true });
            }}
          />
        )}
      </Box>
    </Box>
  );
}

function ChartRowElementOption(props: ChartRowElementOptionProps) {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { name, label, type: _type, imported, ...styleProps } = props;
  const { control } = useFormContext();

  if (imported)
    return (
      <ChartRowElementImportPopover name={name} {...imported}>
        <FormChartOption
          name={name}
          label={label}
          control={control}
          {...styleProps}
        />
      </ChartRowElementImportPopover>
    );

  return (
    <FormChartOption
      name={name}
      label={label}
      control={control}
      {...styleProps}
    />
  );
}

function ChartRowElementCrossOption(props: ChartRowElementCrossOptionProps) {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { name, label, type: _type, ...styleProps } = props;
  const { control } = useFormContext();

  if (props.imported)
    return (
      <ChartRowElementImportPopover name={name} {...props.imported}>
        <FormCrossChartOption
          name={name}
          label={label}
          control={control}
          {...styleProps}
          color={IMPORTED_TYPE_COLORS[props.imported.type || "nursing"]}
        />
      </ChartRowElementImportPopover>
    );

  return (
    <FormCrossChartOption
      name={name}
      label={label}
      control={control}
      {...styleProps}
    />
  );
}

function ChartRowElementCircleOption(props: ChartRowElementCircleOptionProps) {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { type: _type, imported, ...restProps } = props;

  if (imported)
    return (
      <ChartRowElementImportPopover name={restProps.name} {...imported}>
        <ChartRowElementOption
          w="35px"
          h="35px"
          borderRadius="50%"
          type="option"
          {...restProps}
        />
      </ChartRowElementImportPopover>
    );

  return (
    <ChartRowElementOption
      w="35px"
      h="35px"
      borderRadius="50%"
      type="option"
      {...restProps}
    />
  );
}

function ChartRowElementDate(props: ChartRowElementDateProps) {
  const {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    type: _type,
    dateFormat = "MM/dd/yyyy HH:mm",
    showTimeInput = true,
    name,
    size,
    isDisabled,
    imported,
    ...restProps
  } = props;
  const { control, setValue, getValues } = useFormContext();
  const boxRef = React.useRef<HTMLDivElement>(null);

  if (imported)
    return (
      <ChartRowElementImportPopover name={name} {...imported}>
        <Box ref={boxRef} width={size && chartMeasures.CHART_INPUT_WIDTH[size]}>
          <FormCalendarInput
            name={name}
            dateFormat={dateFormat}
            control={control}
            showIcon={false}
            showTimeInput={showTimeInput}
            shouldCloseOnSelect={false}
            disabled={isDisabled}
            onInputClick={() => {
              const date = getValues(name);
              if (!date || !isValid(new Date(date))) {
                setValue(name, new Date());
              }
            }}
          />
        </Box>
      </ChartRowElementImportPopover>
    );

  return (
    <Box
      ref={boxRef}
      width={size && chartMeasures.CHART_INPUT_WIDTH[size]}
      {...restProps}
    >
      <FormCalendarInput
        name={name}
        dateFormat={dateFormat}
        control={control}
        showIcon={false}
        showTimeInput={showTimeInput}
        shouldCloseOnSelect={false}
        disabled={isDisabled}
        onInputClick={() => {
          const date = getValues(name);
          if (!date || !isValid(new Date(date))) {
            setValue(name, new Date());
          }
        }}
      />
    </Box>
  );
}

function ChartRowElementMaskDate(props: ChartRowElementMaskDateProps) {
  const {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    type: _type,
    name,
    size,
    isDisabled,
    imported,
    ...restProps
  } = props;
  const { control, setValue, getValues } = useFormContext();
  const {
    field: { ref, value, onChange, onBlur: fieldOnBlur },
  } = useController({ name, control });

  const formatDate = React.useCallback((val: unknown): string => {
    if (!val) return "";
    if (val instanceof Date && !Number.isNaN(val.getTime())) {
      const m = String(val.getMonth() + 1).padStart(2, "0");
      const d = String(val.getDate()).padStart(2, "0");
      const y = val.getFullYear();
      const h = String(val.getHours()).padStart(2, "0");
      const min = String(val.getMinutes()).padStart(2, "0");
      return `${m}/${d}/${y} ${h}:${min}`;
    }
    if (typeof val === "string") {
      const parsed = new Date(val);
      if (!Number.isNaN(parsed.getTime())) {
        const m = String(parsed.getMonth() + 1).padStart(2, "0");
        const d = String(parsed.getDate()).padStart(2, "0");
        const y = parsed.getFullYear();
        const h = String(parsed.getHours()).padStart(2, "0");
        const min = String(parsed.getMinutes()).padStart(2, "0");
        return `${m}/${d}/${y} ${h}:${min}`;
      }
      return val;
    }
    return "";
  }, []);

  const displayValue = formatDate(value);

  const handleBlur = React.useCallback(() => {
    const raw = formatDate(getValues(name));
    const digits = raw.replace(/[^0-9]/g, "");

    if (digits.length === 0) {
      setValue(name, "", { shouldDirty: true, shouldValidate: true });
      return;
    }

    if (digits.length < 8) {
      setValue(name, "", { shouldDirty: true, shouldValidate: true });
      return;
    }

    const month = Number(digits.slice(0, 2));
    const day = Number(digits.slice(2, 4));
    const year = Number(digits.slice(4, 8));
    const hour = digits.length >= 10 ? Number(digits.slice(8, 10)) : 0;
    const minute = digits.length >= 12 ? Number(digits.slice(10, 12)) : 0;

    if (
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31 ||
      hour < 0 ||
      hour > 23 ||
      minute < 0 ||
      minute > 59
    ) {
      setValue(name, "", { shouldDirty: true, shouldValidate: true });
      return;
    }

    const parsed = new Date(year, month - 1, day, hour, minute);
    if (
      parsed.getFullYear() !== year ||
      parsed.getMonth() !== month - 1 ||
      parsed.getDate() !== day
    ) {
      setValue(name, "", { shouldDirty: true, shouldValidate: true });
      return;
    }

    setValue(name, parsed, { shouldDirty: true, shouldValidate: true });
  }, [formatDate, getValues, name, setValue]);

  const handleFocus = React.useCallback(() => {
    const current = getValues(name);
    if (
      !current ||
      (typeof current === "string" && !current.trim()) ||
      (current instanceof Date && Number.isNaN(current.getTime())) ||
      current === "__/__/____ __:__"
    ) {
      onChange(formatDate(new Date()));
    }
  }, [formatDate, getValues, name, onChange]);

  const inputWidth = size && chartMeasures.CHART_INPUT_WIDTH[size];

  const maskedInput = (
    <InputMask
      mask="99/99/9999 99:99"
      maskPlaceholder="__/__/____ 00:00"
      value={displayValue}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.value);
      }}
      onFocus={() => {
        handleFocus();
      }}
      onBlur={() => {
        fieldOnBlur();
        handleBlur();
      }}
      disabled={isDisabled}
    >
      {
        ((inputProps: React.InputHTMLAttributes<HTMLInputElement>) => {
          const { size: _htmlSize, disabled, ...filteredProps } = inputProps;
          return (
            <Input
              ref={ref}
              {...filteredProps}
              isDisabled={isDisabled}
              autoComplete="off"
              bg="white"
              borderColor="gray.700"
              color="blue"
              border="1px solid"
            />
          );
        }) as unknown as React.ReactNode
      }
    </InputMask>
  );

  if (imported)
    return (
      <ChartRowElementImportPopover name={name} {...imported}>
        <Box width={inputWidth}>{maskedInput}</Box>
      </ChartRowElementImportPopover>
    );

  return (
    <Box width={inputWidth} {...restProps}>
      {maskedInput}
    </Box>
  );
}

function ChartRowElementCheckbox(props: ChartRowElementCheckboxProps) {
  const { name, imported, ...restProps } = props;
  const { register, watch } = useFormContext();
  const value = watch(name);
  const boolValue = value === true || value === "true";
  const checkboxProps = restProps;

  if (imported)
    return (
      <ChartRowElementImportPopover name={name} {...imported}>
        <Checkbox
          {...register(name)}
          isChecked={boolValue}
          {...checkboxProps}
        />
      </ChartRowElementImportPopover>
    );

  return (
    <Checkbox {...register(name)} isChecked={boolValue} {...checkboxProps} />
  );
}

function ChartRowElementSwitch(props: ChartRowElementSwitchProps) {
  const { name, ...restProps } = props;
  const { register, watch } = useFormContext();
  const value = watch(name);
  const boolValue = value === true || value === "true";
  const switchProps = restProps;

  if (props.imported)
    return (
      <ChartRowElementImportPopover name={name} {...props.imported}>
        <Switch
          {...register(name)}
          isChecked={boolValue}
          colorScheme="green"
          size="lg"
          {...switchProps}
        />
      </ChartRowElementImportPopover>
    );

  return (
    <Switch
      {...register(name)}
      isChecked={boolValue}
      colorScheme="green"
      size="lg"
      {...switchProps}
    />
  );
}

function ChartRowElementCheckboxButton(
  props: ChartRowElementCheckboxButtonProps
) {
  const { children, name, type: _type, imported, ...styleProps } = props;
  const { control } = useFormContext();

  if (imported)
    return (
      <ChartRowElementImportPopover name={name} {...imported}>
        <FormCheckboxButton
          name={name}
          control={control}
          {...styleProps}
          activeColor={IMPORTED_TYPE_COLORS[imported.type || "nursing"]}
        >
          {children}
        </FormCheckboxButton>
      </ChartRowElementImportPopover>
    );

  return (
    <FormCheckboxButton name={name} control={control} {...styleProps}>
      {children}
    </FormCheckboxButton>
  );
}

function ChartRowElementCounterOption(
  props: ChartRowElementCounterOptionProps
) {
  const { children, imported, ...elementProps } = props;

  if (imported)
    return (
      <ChartRowElementImportPopover name={elementProps.name} {...imported}>
        <Flex direction="row" alignItems="center">
          <FormCounterInputOption {...elementProps} />
        </Flex>
      </ChartRowElementImportPopover>
    );

  return (
    <Flex direction="row" alignItems="center">
      <FormCounterInputOption {...elementProps} />
    </Flex>
  );
}

function ChartRowElement(props: ChartRowElementProps) {
  const { isPastEncounter, isCurrentChartCompleted } =
    useChartTrackingContext();

  const isCompletedChartLocked = isCurrentChartCompleted;
  const isBaseDisabled = isPastEncounter || isCompletedChartLocked;

  if (isChartRowElementT<ChartRowElementLabelProps>(props, "label")) {
    return <ChartRowElementLabel {...props} />;
  }

  if (isChartRowElementT<ChartRowElementContainerProps>(props, "container")) {
    return <ChartRowElementContainer {...props} />;
  }

  if (isChartRowElementT<ChartRowElementOptionProps>(props, "option")) {
    return (
      <ChartRowElementOption
        {...props}
        disabled={props.disabled || isBaseDisabled}
      />
    );
  }

  if (
    isChartRowElementT<ChartRowElementCrossOptionProps>(props, "cross-option")
  ) {
    return (
      <ChartRowElementCrossOption
        {...props}
        disabled={props.disabled || props.isDisabled || isBaseDisabled}
      />
    );
  }

  if (
    isChartRowElementT<ChartRowElementCircleOptionProps>(props, "circle-option")
  ) {
    return (
      <ChartRowElementCircleOption
        {...props}
        disabled={props.disabled || isBaseDisabled}
      />
    );
  }

  if (isChartRowElementT<ChartRowElementTextAreaProps>(props, "text-area")) {
    return (
      <ChartRowElementTextArea
        {...props}
        isDisabled={props.isDisabled || isBaseDisabled}
      />
    );
  }

  if (isChartRowElementT<ChartRowElementRichTextProps>(props, "rich-text")) {
    return (
      <ChartRowElementRichText
        {...props}
        isDisabled={props.isDisabled || isBaseDisabled}
      />
    );
  }

  if (isChartRowElementT<ChartRowElementEmptyProps>(props, "empty")) {
    return <ChartRowElementEmpty {...props} />;
  }

  if (isChartRowElementT<ChartRowElementDateProps>(props, "date")) {
    return (
      <ChartRowElementDate
        {...props}
        isDisabled={props.isDisabled || isBaseDisabled}
      />
    );
  }

  if (isChartRowElementT<ChartRowElementMaskDateProps>(props, "mask-date")) {
    return (
      <ChartRowElementMaskDate
        {...props}
        isDisabled={props.isDisabled || isBaseDisabled}
      />
    );
  }

  if (isChartRowElementT<ChartRowElementCheckboxProps>(props, "checkbox")) {
    return (
      <ChartRowElementCheckbox
        {...props}
        isDisabled={props.isDisabled || isBaseDisabled}
      />
    );
  }

  if (isChartRowElementT<ChartRowElementSwitchProps>(props, "switch")) {
    return (
      <ChartRowElementSwitch
        {...props}
        isDisabled={props.isDisabled || isBaseDisabled}
      />
    );
  }

  if (
    isChartRowElementT<ChartRowElementCheckboxButtonProps>(
      props,
      "checkbox-button"
    )
  ) {
    return (
      <ChartRowElementCheckboxButton
        {...props}
        isDisabled={props.isDisabled || isBaseDisabled}
      />
    );
  }

  if (
    isChartRowElementT<ChartRowElementCounterOptionProps>(
      props,
      "counter-option"
    )
  ) {
    return (
      <ChartRowElementCounterOption
        {...props}
        isDisabled={props.isDisabled || isBaseDisabled}
      />
    );
  }

  return (
    <ChartRowElementInput
      {...props}
      isDisabled={props.isDisabled || isBaseDisabled}
    />
  );
}

export { ChartRowElement, ChartRowElementInput };
export type { ChartRowElementImportedProps, ChartRowElementProps };
